import { Typography } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { Outlet, useParams } from "react-router-dom";
import { DateRangeReportFilter, IdReportFilter, RecencyReportFilter } from "../../api/dtos";
import {
    DetailsContent,
    DetailsHeader,
    DetailsPanel,
    GridData,
    GridLabel,
    GridRow,
} from "../../components/DetailsPanel";
import SetLinks from "../../components/layout/SetLinks";
import CollapsibleAppTable from "../../components/tables/CollapsibleAppTable";
import TitleBox from "../../components/TitleBox";
import ReportTemplatePreviewContext from "../../contexts/ReportTemplatePreviewContext";
import useReportTemplateById from "../../hooks/data/useReportTemplateById";
import useReportTemplateFieldsByTemplateId from "../../hooks/data/useReportTemplateFieldsByTemplateId";
import useReportCustomTemplateFields from "../../hooks/data/useReportCustomTemplateFields";
import useReportTemplatePreviewByIdFilters from "../../hooks/data/useReportTemplatePreviewByIdFilters";
import type { ReportColumnRow } from "./ReportColumnDefinitions";
import { buildTemplatePreviewColumns, createReportTableRows } from "./ReportPreview.model";
import ReportTemplatePreviewFilters from "./ReportTemplatePreviewFilters";

const ReportTemplatePreview: FC = () => {
    const params = useParams();
    const templateId = Number(params.templateId);

    const { data: template } = useReportTemplateById(templateId);
    const { data: fields } = useReportTemplateFieldsByTemplateId(templateId);
    const { data: fieldsWithNames } = useReportCustomTemplateFields();
    const [idFilters, setIdFilters] = useState<IdReportFilter[]>([]);
    const [dateRangeFilters, setDateRangeFilters] = useState<DateRangeReportFilter[]>([]);
    const [recencyFilters, setRecencyFilters] = useState<RecencyReportFilter[]>([]);

    const contextData = useReportTemplatePreviewByIdFilters(
        template?.reportTemplate?.id,
        idFilters,
        dateRangeFilters,
        recencyFilters,
    );
    const { data: data } = contextData;

    useEffect(() => {
        setIdFilters([]);
        setDateRangeFilters([]);
        setRecencyFilters([]);
    }, [template?.reportTemplate?.templateType]);

    const sourceRows = useMemo(() => createReportTableRows((data?.rows ?? []) as ReportColumnRow[]), [data?.rows]);
    const templateType = template?.reportTemplate?.templateType;
    const columns = useMemo(
        () => buildTemplatePreviewColumns(templateType, sourceRows.rows, templateId, fields, fieldsWithNames),
        [templateType, sourceRows.rows, templateId, fields, fieldsWithNames],
    );

    const exportMetadata = useMemo(
        () => ({
            fileName: `${template?.reportTemplate?.name ?? "Unknown Report Template"} - Preview`,
            exportTitle: `${template?.reportTemplate?.name ?? "Unknown Report Template"} - Preview`,
            includeDateTime: true,
        }),
        [template?.reportTemplate?.name],
    );

    return (
        <ReportTemplatePreviewContext.Provider value={contextData}>
            <SetLinks stageLinkEnabled={false} lotLinkEnabled={false} />
            <Outlet />
            <TitleBox>
                <Typography variant="h2">{template?.reportTemplate?.name}</Typography>
            </TitleBox>
            {
                <ReportTemplatePreviewFilters
                    templateType={template?.reportTemplate?.templateType}
                    idFilters={idFilters}
                    setIdFilters={setIdFilters}
                    dateRangeFilters={dateRangeFilters}
                    setDateRangeFilters={setDateRangeFilters}
                    recencyFilters={recencyFilters}
                    setRecencyFilters={setRecencyFilters}
                />
            }
            {data?.summaries &&
                data.summaries.map((summary, index) => (
                    <DetailsPanel key={index}>
                        <DetailsHeader>Summary</DetailsHeader>
                        <DetailsContent>
                            {Object.keys(summary).map((key, index) => (
                                <GridRow key={index}>
                                    <GridLabel>{key.replace(/([A-Z])/g, " $1").trim()}</GridLabel>
                                    <GridData>{summary[key]}</GridData>
                                </GridRow>
                            ))}
                        </DetailsContent>
                    </DetailsPanel>
                ))}
            <CollapsibleAppTable
                title={"Entries"}
                loading={false}
                columns={columns}
                density="compact"
                rows={sourceRows.rows}
                getRowId={sourceRows.getRowId}
                columnControls={{
                    menu: true,
                    resize: true,
                }}
                filterControls
                export={exportMetadata}
            />
        </ReportTemplatePreviewContext.Provider>
    );
};

export default ReportTemplatePreview;
